import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import api from '@/utils/api';
import { RankBadge } from '@/utils/ranks';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { ArrowLeft, Plus, Eye, MessageCircle, Pin, Lock, X } from 'lucide-react';

function ThreadSkeleton() {
  return (
    <div className="glass-card rounded-xl p-4 flex items-center gap-4 animate-pulse">
      <div className="flex-1 min-w-0 space-y-2">
        <div className="h-4 bg-sacred-gold/10 rounded w-2/5" />
        <div className="h-3 bg-sacred-gold/5 rounded w-1/4" />
      </div>
      <div className="hidden sm:flex items-center gap-4 shrink-0">
        <div className="h-3 w-8 bg-sacred-gold/5 rounded" />
        <div className="h-3 w-8 bg-sacred-gold/5 rounded" />
      </div>
    </div>
  );
}

export default function ForumCategory() {
  const { slug } = useParams();
  const { t, localizedField } = useLang();
  const { user } = useAuth();
  const toast = useGravityToast();
  const [threads, setThreads] = useState([]);
  const [category, setCategory] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, threadRes] = await Promise.all([
          api.get('/forum/categories'),
          api.get(`/forum/threads?category=${slug}`),
        ]);
        const cat = catRes.data.find(c => c.slug === slug);
        setCategory(cat);
        setThreads(threadRes.data.threads || []);
      } catch {}
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!category) return;
    try {
      const { data } = await api.post('/forum/threads', {
        category_id: category.id, title: newTitle, content: newContent
      });
      setThreads(prev => [data, ...prev]);
      setShowNew(false);
      setNewTitle('');
      setNewContent('');
      toast.success('Thread created');
    } catch { toast.error('Failed to create thread'); }
  };

  return (
    <div data-testid="forum-category-page" className="min-h-screen py-20 md:py-32 px-4">
      <div className="max-w-4xl mx-auto">
        <ScrollReveal>
          <Link to="/forum" className="inline-flex items-center gap-2 text-sacred-gold font-body text-sm mb-6 hover:text-molten-gold">
            <ArrowLeft size={16} /> {t('forum.title')}
          </Link>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-display text-heading flex items-center gap-3">
                {category?.icon} {localizedField(category?.name)}
              </h1>
              <p className="text-text-muted/70 text-sm font-body mt-1">{localizedField(category?.description)}</p>
            </div>
            {user && user.has_password && (
              <button onClick={() => setShowNew(true)} className="gold-button px-4 py-2 rounded-full font-body text-sm flex items-center gap-2" data-testid="new-thread-btn">
                <Plus size={16} /> {t('forum.newThread')}
              </button>
            )}
            {user && !user.has_password && (
              <Link to="/auth/set-password" className="glass-button px-4 py-2 rounded-full font-body text-xs flex items-center gap-2 text-sacred-gold" data-testid="set-password-link">
                <Lock size={14} /> Set a password to post
              </Link>
            )}
          </div>
        </ScrollReveal>

        {showNew && (
          <div className="glass-card rounded-2xl p-6 mb-8" data-testid="new-thread-form">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-display text-heading">{t('forum.createThread')}</h3>
              <button onClick={() => setShowNew(false)} className="text-dust-gray hover:text-heading"><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder={t('forum.threadTitle')} required className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="thread-title-input" />
              <textarea value={newContent} onChange={e => setNewContent(e.target.value)} placeholder={t('forum.content')} required rows={4} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm resize-none" data-testid="thread-content-input" />
              <button type="submit" className="gold-button px-5 py-2 rounded-full font-body text-sm" data-testid="thread-submit">{t('forum.createThread')}</button>
            </form>
          </div>
        )}

        <div className="space-y-3">
          {loading ? (
            <>
              <ThreadSkeleton />
              <ThreadSkeleton />
              <ThreadSkeleton />
              <ThreadSkeleton />
              <ThreadSkeleton />
            </>
          ) : threads.length === 0 ? (
            <div className="glass-card rounded-2xl p-8 text-center">
              <MessageCircle size={40} className="text-sacred-gold/20 mx-auto mb-3" />
              <p className="text-text-muted font-body">{t('common.noResults')}</p>
            </div>
          ) : threads.map((thread, i) => (
            <ScrollReveal key={thread.id} delay={i * 0.05}>
              <Link to={`/forum/thread/${thread.id}`} className="glass-card rounded-xl p-4 flex items-center gap-4 group" data-testid={`thread-${thread.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {thread.is_pinned && <Pin size={12} className="text-sacred-gold" />}
                    {thread.is_locked && <Lock size={12} className="text-dust-gray" />}
                    <h3 className="font-body font-medium text-heading group-hover:text-sacred-gold transition-colors truncate">{localizedField(thread.title)}</h3>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-text-muted/50 font-body">
                    <span>{thread.user?.display_name}</span>
                    {thread.user?.rank && <RankBadge rank={thread.user.rank} size="sm" />}
                    <span>{new Date(thread.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-text-muted/50 text-xs font-body shrink-0">
                  <span className="flex items-center gap-1"><MessageCircle size={12} /> {thread.reply_count || 0}</span>
                  <span className="flex items-center gap-1"><Eye size={12} /> {thread.view_count || 0}</span>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </div>
  );
}
